/**
 * Each marketplace's app-icon mark, drawn in its own brand colours so a row
 * reads "listed on Depop" at a glance. SVG attributes only (no inline styles,
 * which the CSP forbids). A code with no mark falls back to its first letter.
 */
import type { ReactNode } from "react";

interface Mark {
  bg: string;
  /** A light tile gets a hairline so it holds its edge on white. */
  light?: boolean;
  glyph: ReactNode;
}

const SANS = "Archivo Variable, Archivo, Helvetica Neue, Arial, sans-serif";
const SERIF = "Georgia, Times New Roman, serif";

function letter(ch: string, opts: { font?: string; weight?: number; size?: number; italic?: boolean; y?: number; fill?: string } = {}) {
  return (
    <text x="12" y={opts.y ?? 16.6} textAnchor="middle" fontFamily={opts.font ?? SANS} fontWeight={opts.weight ?? 800}
      fontSize={opts.size ?? 14} fontStyle={opts.italic ? "italic" : undefined} fill={opts.fill ?? "#fff"}>
      {ch}
    </text>
  );
}

const MARKS: Record<string, Mark> = {
  ebay: {
    bg: "#ffffff",
    light: true,
    glyph: (
      <text x="12" y="15" textAnchor="middle" fontFamily={SANS} fontWeight={700} fontSize={9} letterSpacing="-0.4">
        <tspan fill="#e53238">e</tspan><tspan fill="#0064d2">b</tspan><tspan fill="#f5af02">a</tspan><tspan fill="#86b817">y</tspan>
      </text>
    ),
  },
  depop: { bg: "#ff2300", glyph: letter("d", { size: 16, y: 16.4 }) },
  poshmark: { bg: "#7f0353", glyph: letter("P", { font: SERIF, weight: 700, size: 15 }) },
  mercari: { bg: "#5356ee", glyph: letter("m", { size: 15, y: 16 }) },
  vinted: { bg: "#09b1ba", glyph: letter("V", { font: SERIF, weight: 700, size: 15, italic: true }) },
  facebook: { bg: "#0866ff", glyph: letter("f", { size: 17, y: 18.6 }) },
  etsy: { bg: "#f1641e", glyph: letter("E", { font: SERIF, weight: 400, size: 15 }) },
  grailed: { bg: "#000000", glyph: letter("G", { weight: 800, size: 13, y: 16.4 }) },
  local: {
    bg: "#6a7780",
    glyph: (
      <g fill="none" stroke="#fff" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 10.5 7 6.5h10l1 4" /><path d="M6 10.5h12v7H6z" /><path d="M10.5 17.5v-3.5h3v3.5" />
      </g>
    ),
  },
};

export function MarketplaceLogo({ code, size = 22, title }: { code: string; size?: number; title?: string }) {
  const mark = MARKS[code];
  const radius = 6;
  return (
    <svg className="mp-logo" width={size} height={size} viewBox="0 0 24 24" role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true} aria-label={title}>
      {title && <title>{title}</title>}
      <rect x="0.5" y="0.5" width="23" height="23" rx={radius} fill={mark?.bg ?? "#6a7780"}
        stroke={mark?.light ? "#dde2df" : "none"} />
      {mark?.glyph ?? letter(code.slice(0, 1).toUpperCase())}
    </svg>
  );
}
